/**
 * Scheduler service — reads + writes to the in-memory store.
 *
 * The pure scheduling logic lives in `schedulerEngine.ts`. This module
 * fetches inputs from the store, calls the engine, and (when committed)
 * persists the result.
 */

import type { Game, Id, Iso8601, Rink, ScheduleSlot } from '../models';
import { getDb, updateDb } from './store';
import { newId, nowIso } from './ids';
import {
  generateSchedule,
  type ScheduleProposal,
} from './schedulerEngine';
import { generateSeasonSchedule } from './seasonSchedulerService';
import { listDivisions, listTeams } from './rosterService';
import { getTournament } from './tournamentService';

export function listScheduleSlots(tournamentId: Id): ScheduleSlot[] {
  return getDb()
    .scheduleSlots.filter((s) => s.tournamentId === tournamentId)
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt));
}

export function listGames(tournamentId: Id): Game[] {
  const slots = new Map(listScheduleSlots(tournamentId).map((s) => [s.id, s]));
  return getDb()
    .games.filter((g) => g.tournamentId === tournamentId)
    .sort((a, b) => {
      const sa = a.scheduleSlotId ? slots.get(a.scheduleSlotId)?.startsAt ?? '' : '';
      const sb = b.scheduleSlotId ? slots.get(b.scheduleSlotId)?.startsAt ?? '' : '';
      return sa.localeCompare(sb);
    });
}

export function getGame(id: Id): Game | undefined {
  return getDb().games.find((g) => g.id === id);
}

export function listRinksForTournament(tournamentId: Id): Rink[] {
  const t = getTournament(tournamentId);
  if (!t) return [];
  const all = getDb().rinks;
  const venueRinks = all.filter((r) => r.venueId === t.primaryVenueId);
  return (venueRinks.length > 0 ? venueRinks : all).sort((a, b) => a.rank - b.rank);
}

export function ensureRinksForTournament(tournamentId: Id, count: number, namePattern = 'Rink {{n}}'): Rink[] {
  const t = getTournament(tournamentId);
  if (!t || !t.primaryVenueId) return listRinksForTournament(tournamentId);
  const existing = listRinksForTournament(tournamentId);
  if (existing.length >= count) return existing;
  const ts = nowIso();
  const toAdd: Rink[] = [];
  for (let i = existing.length; i < count; i++) {
    toAdd.push({
      id: newId('rink'),
      kind: 'rink',
      createdAt: ts,
      updatedAt: ts,
      venueId: t.primaryVenueId,
      name: formatRinkName(namePattern, i + 1),
      rank: i + 1,
      capacity: 1,
    });
  }
  updateDb((db) => ({ ...db, rinks: [...db.rinks, ...toAdd] }));
  return [...existing, ...toAdd];
}

function formatRinkName(pattern: string, rank: number): string {
  const clean = pattern.trim() || 'Rink {{n}}';
  return clean.replace(/\{\{\s*n\s*\}\}/gi, String(rank));
}

/** Run the engine without persisting. */
export function previewSchedule(tournamentId: Id): ScheduleProposal {
  const tournament = getTournament(tournamentId);
  if (!tournament) {
    return { games: [], slots: [], warnings: [], stats: { perTeam: {} } };
  }
  const divisions = listDivisions(tournamentId);
  const teams = listTeams(tournamentId);
  const rinks = listRinksForTournament(tournamentId);
  if (tournament.format === 'season') {
    const r = generateSeasonSchedule({ tournament, divisions, teams, rinks });
    return { games: r.games, slots: r.slots, warnings: r.warnings, stats: { perTeam: {} } };
  }
  return generateSchedule({ tournament, divisions, teams, rinks });
}

/** Persist a proposal — overwrites existing schedule for the tournament. */
export function commitSchedule(tournamentId: Id, proposal: ScheduleProposal): void {
  const ts = nowIso();
  const slots: ScheduleSlot[] = proposal.slots.map((s) => ({
    ...s,
    kind: 'schedule-slot',
    createdAt: ts,
    updatedAt: ts,
  }));
  const games: Game[] = proposal.games.map((g) => ({
    ...g,
    kind: 'game',
    createdAt: ts,
    updatedAt: ts,
  }));
  updateDb((db) => {
    const newGameIds = new Set(games.map((g) => g.id));
    const otherTournamentGameIds = new Set(
      db.games.filter((g) => g.tournamentId !== tournamentId).map((g) => g.id),
    );
    return {
      ...db,
      scheduleSlots: [
        ...db.scheduleSlots.filter((s) => s.tournamentId !== tournamentId),
        ...slots,
      ],
      games: [...db.games.filter((g) => g.tournamentId !== tournamentId), ...games],
      gameAssignments: db.gameAssignments.filter(
        (a) => newGameIds.has(a.gameId) || otherTournamentGameIds.has(a.gameId),
      ),
    };
  });
}

export function clearSchedule(tournamentId: Id): void {
  updateDb((db) => ({
    ...db,
    scheduleSlots: db.scheduleSlots.filter((s) => s.tournamentId !== tournamentId),
    games: db.games.filter((g) => g.tournamentId !== tournamentId),
  }));
}

export function updateGameSchedule(
  gameId: Id,
  patch: { startsAt: Iso8601; rinkId: Id },
): Game | undefined {
  const db = getDb();
  const game = db.games.find((candidate) => candidate.id === gameId);
  if (!game) return undefined;
  const tournament = getTournament(game.tournamentId);
  const durationMinutes = tournament?.settings.scoring.gameLengthMinutes ?? 12;
  const startsAt = patch.startsAt;
  const endsAt = new Date(new Date(startsAt).getTime() + durationMinutes * 60_000).toISOString();
  const ts = nowIso();
  let slotId = game.scheduleSlotId;
  if (!slotId) slotId = newId('slot');
  const slot: ScheduleSlot = {
    id: slotId,
    kind: 'schedule-slot',
    createdAt: db.scheduleSlots.find((candidate) => candidate.id === slotId)?.createdAt ?? ts,
    updatedAt: ts,
    tournamentId: game.tournamentId,
    rinkId: patch.rinkId,
    startsAt,
    endsAt,
  };
  updateDb((store) => ({
    ...store,
    scheduleSlots: [...store.scheduleSlots.filter((candidate) => candidate.id !== slot.id), slot],
    games: store.games.map((candidate) =>
      candidate.id === gameId ? { ...candidate, scheduleSlotId: slot.id, status: candidate.status === 'postponed' ? 'scheduled' : candidate.status, updatedAt: ts } : candidate,
    ),
  }));
  return getGame(gameId);
}

/** Swap the time/rink of two games (keeps everything else). */
export function swapGameSlots(gameAId: Id, gameBId: Id): void {
  const db = getDb();
  const a = db.games.find((g) => g.id === gameAId);
  const b = db.games.find((g) => g.id === gameBId);
  if (!a || !b) return;
  const aSlot = a.scheduleSlotId;
  const bSlot = b.scheduleSlotId;
  updateDb((d) => ({
    ...d,
    games: d.games.map((g) => {
      if (g.id === gameAId) return { ...g, scheduleSlotId: bSlot, updatedAt: nowIso() };
      if (g.id === gameBId) return { ...g, scheduleSlotId: aSlot, updatedAt: nowIso() };
      return g;
    }),
  }));
}
